import abortControllerTypes from '../../consts/abortControllerTypes';
import AssetIdError from '../../errors/assetIdError';
import AuthTokenError from '../../errors/authTokenError';
import {selectIsOnline} from '../../state/selectors/global';
import {selectIotDeviceAssetId} from '../../state/selectors/iotDevices';
import {getState} from '../../state/store';
import stringUtils from '../../utils/stringUtils';
import awsAuthService from '../aws/awsAuthService';
import log from '../logger/log';
import {sendRequest} from '../offlineRequestService';
import damTokenService from './damTokenService';

export const createClient = (config) => {
    const {
        defaultEndpoint,
        createHeaders,
        clientError,
        logPrefix,
        getToken = getDefaultToken,
        withCredentials,
    } = config;

    const callRequest = async (args) => {
        const {
            endpoint = defaultEndpoint,
            method,
            path,
            body,
            mapping,
            isUamToken,
            serialNumber,
            queryParams,
            pathParams,
            isThrowError,
            isCached,
            isOfflineResponse,
            isOnlineRequest,
            isWaitingCache,
            isImportantRequest,
            isLogResponse = true,
            signal,
        } = args;

        let url = path;

        try {
            const token = await getToken(args);

            url = formatUrl(endpoint, path, serialNumber, queryParams, pathParams);

            const requestConfig = createRequestConfig({
                method,
                url,
                token,
                body,
                signal,
                createHeaders,
                isUamToken,
                withCredentials,
            });

            log.info(`${logPrefix}: Request has started. Method: ${method}. Url: ${url}.`, serialNumber);

            const response = await sendRequest({
                config: requestConfig,
                isOffline: !selectIsOnline(getState()), // not in the service: import order matters
                isOnlineRequest,
                isWaitingCache,
                isOfflineResponse,
                isCached,
                args,
                logPrefix,
            });

            const responseLog = isLogResponse ? ` Response: ${JSON.stringify(response)}` : '';

            log.info(`${logPrefix}: Successfully received response. Url: ${url}.${responseLog}`, serialNumber);

            return mapping ? mapping(response) : response;
        } catch (e) {
            if (e?.code === abortControllerTypes.ERR_CANCELED) {
                log.warn(`${logPrefix}: Request aborted. Url: ${url}`, serialNumber);

                throw e;
            }

            const {message, response} = e || {};

            log.error(
                `${logPrefix}: Request failed. Url: ${url}. Status: ${response?.status}. Message: ${JSON.stringify(
                    response?.data?.error
                )}`,
                message,
                serialNumber
            );

            await clientError.handleRequestError({e, url, isUamToken, isThrowError, isImportantRequest, args});
        }
    };

    const callGet = (args) => callRequest({method: 'get', ...args});
    const callPost = (args) => callRequest({method: 'post', ...args});
    const callPut = (args) => callRequest({method: 'put', ...args});
    const callDel = (args) => callRequest({method: 'delete', ...args});

    return {
        callRequest,
        callGet,
        callPost,
        callPut,
        callDel,
    };
};

const getDefaultToken = async ({isUamToken, serialNumber, isCached}) => {
    if (isCached) return;

    const token = isUamToken
        ? await damTokenService.getUserAuthToken(serialNumber)
        : await awsAuthService.getCognitoIdToken();

    if (!token) throw new AuthTokenError();

    return token;
};

const createRequestConfig = ({method, url, token, body, signal, createHeaders, isUamToken, withCredentials}) => {
    const config = {
        method,
        url,
        headers: createHeaders(token, isUamToken),
        signal,
        withCredentials,
    };

    if (body) config.data = body;

    return config;
};

const formatUrl = (endpoint, path, serialNumber, queryParams, pathParams = []) => {
    const url = new URL(endpoint);
    let assetId;

    if (serialNumber) {
        assetId = selectIotDeviceAssetId(serialNumber)(getState());

        if (!assetId) {
            throw new AssetIdError();
        }
    }

    // Append to pathname as API_NAME might include part of the pathname
    url.pathname += stringUtils.formatString(path, assetId, ...pathParams);

    if (queryParams) {
        // Add request params manually to cache url with params
        url.search = new URLSearchParams(queryParams).toString();
    }

    return url.toString();
};
